import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Filter, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/utils/datetime";

type Row = {
    id: string; action: string; entity: string; entity_id: string | null;
    actor_user_id: string | null; actor_name: string | null;
    at: string; summary: Record<string, any>; ip_address: string | null;
};

export default function AuditViewer() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canView = hasPerm("audit.view");
    const [items, setItems] = useState<Row[]>([]);
    const [total, setTotal] = useState(0);
    const [entities, setEntities] = useState<string[]>([]);
    const [actions, setActions] = useState<string[]>([]);
    const [entity, setEntity] = useState("");
    const [action, setAction] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [q, setQ] = useState("");
    const [offset, setOffset] = useState(0);
    const [detail, setDetail] = useState<Row | null>(null);
    const limit = 25;
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const params = new URLSearchParams();
            params.set("limit", String(limit));
            params.set("offset", String(offset));
            if (entity) params.set("entity", entity);
            if (action) params.set("action", action);
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            if (q) params.set("q", q);
            const [d, f] = await Promise.all([api.get(`/audit?${params}`), api.get("/audit/entities")]);
            setItems(d.data.items);
            setTotal(d.data.total);
            setEntities(f.data.entities);
            setActions(f.data.actions);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    }, [entity, action, dateFrom, dateTo, q, offset]);
    useEffect(() => { load(); }, [load]);

    if (!canView) {
        return <div className="surface-card p-8 text-center text-sm" style={{ color: "var(--text)" }} data-testid="audit-perm-denied">{t("common.no_permission")}</div>;
    }

    return (
        <div className="space-y-6" data-testid="audit-viewer-page">
            <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("audit.title")}</h1>

            <div className="surface-card p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="audit-filters">
                <label className="block">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("audit.entity")}</div>
                    <select data-testid="filter-entity" value={entity} onChange={(e) => { setEntity(e.target.value); setOffset(0); }} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                        <option value="">{t("common.all")}</option>
                        {entities.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                </label>
                <label className="block">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("audit.action")}</div>
                    <select data-testid="filter-action" value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}>
                        <option value="">{t("common.all")}</option>
                        {actions.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </label>
                <label className="block">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("audit.date_from")}</div>
                    <input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}/>
                </label>
                <label className="block">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("audit.date_to")}</div>
                    <input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setOffset(0); }} className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}/>
                </label>
                <label className="block">
                    <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("audit.search")}</div>
                    <input data-testid="filter-q" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder="…" className="surface-input w-full px-3 py-2 text-sm" style={{ minHeight: 40 }}/>
                </label>
            </div>

            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="audit-error">{err}</div>}

            <div className="surface-card overflow-hidden">
                {loading ? <div className="p-8 flex justify-center"><Loader2 className="animate-spin"/></div> :
                    items.length === 0 ? <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="audit-empty">{t("common.empty")}</div> : (
                    <>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                    <th className="px-4 py-2 font-medium">{t("audit.when")}</th>
                                    <th className="px-4 py-2 font-medium">{t("audit.actor")}</th>
                                    <th className="px-4 py-2 font-medium">{t("audit.action")}</th>
                                    <th className="px-4 py-2 font-medium">{t("audit.entity")}</th>
                                    <th className="px-4 py-2 font-medium">{t("audit.summary")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((r) => (
                                    <tr key={r.id} className="border-t cursor-pointer hover:bg-[var(--input)]" style={{ borderColor: "var(--border)" }} onClick={() => setDetail(r)} data-testid={`audit-row-${r.id}`}>
                                        <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{fmtDateTime(r.at)}</td>
                                        <td className="px-4 py-3" style={{ color: "var(--text)" }}>{r.actor_name || "—"}</td>
                                        <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--text)" }}>{r.action}</td>
                                        <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{r.entity}</td>
                                        <td className="px-4 py-3 text-xs truncate max-w-xs" style={{ color: "var(--text-muted)" }}>{Object.keys(r.summary).length > 0 ? JSON.stringify(r.summary).slice(0, 80) : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="flex items-center justify-between px-4 py-3 border-t text-xs" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
                            <span data-testid="audit-total">{offset + 1}–{Math.min(offset + limit, total)} / {total}</span>
                            <div className="flex gap-2">
                                <button data-testid="audit-prev" disabled={offset === 0} onClick={() => setOffset(Math.max(offset - limit, 0))} className="inline-flex items-center gap-1 rounded-md px-2 py-1 border disabled:opacity-40" style={{ borderColor: "var(--border)", color: "var(--text)" }}><ChevronLeft size={12}/> {t("common.back")}</button>
                                <button data-testid="audit-next" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 border disabled:opacity-40" style={{ borderColor: "var(--border)", color: "var(--text)" }}>{t("common.next")} <ChevronRight size={12}/></button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {detail && (
                <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setDetail(null)} data-testid="audit-detail">
                    <div className="surface-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-4">
                            <Info size={16} style={{ color: "var(--accent)" }}/>
                            <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{detail.action}</h2>
                        </div>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("audit.when")}</dt><dd style={{ color: "var(--text)" }}>{fmtDateTime(detail.at)}</dd></div>
                            <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("audit.actor")}</dt><dd style={{ color: "var(--text)" }}>{detail.actor_name || detail.actor_user_id || "—"}</dd></div>
                            <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>{t("audit.entity")}</dt><dd style={{ color: "var(--text)" }}>{detail.entity} {detail.entity_id ? `· ${detail.entity_id.slice(0, 8)}…` : ""}</dd></div>
                            {detail.ip_address && <div className="flex justify-between"><dt style={{ color: "var(--text-muted)" }}>IP</dt><dd className="font-mono text-xs" style={{ color: "var(--text)" }}>{detail.ip_address}</dd></div>}                        </dl>
                        {Object.keys(detail.summary).length > 0 && (
                            <div className="mt-4">
                                <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{t("audit.summary")}</div>
                                <pre className="text-xs p-3 rounded-md overflow-auto" style={{ background: "var(--input)", color: "var(--text)" }} data-testid="audit-summary-json">{JSON.stringify(detail.summary, null, 2)}</pre>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
